import React from 'react'
import { Profile, GuideProfile } from '../lib/supabase'
import { motion } from 'framer-motion'
import { MapPin, Globe, Calendar, Star, Users, Clock } from 'lucide-react'

interface PublicProfileViewProps {
  profile: Profile
  guideProfile?: GuideProfile | null
  isLoading?: boolean
}

export const PublicProfileView: React.FC<PublicProfileViewProps> = ({ 
  profile, 
  guideProfile,
  isLoading = false 
}) => {
  
  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 animate-pulse">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="h-48 bg-gray-300"></div>
          <div className="p-6 space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-24 h-24 bg-gray-300 rounded-full"></div>
              <div className="space-y-2">
                <div className="h-6 bg-gray-300 rounded w-48"></div>
                <div className="h-4 bg-gray-300 rounded w-32"></div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-300 rounded w-full"></div>
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    })
  }

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < Math.floor(rating) 
            ? 'text-yellow-400 fill-current' 
            : 'text-gray-300'
        }`}
      />
    ))
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto p-6"
    >
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        
        {/* Cover/Header Section */}
        <div className="h-48 bg-gradient-to-r from-green-400 to-blue-500 relative">
          <div className="absolute inset-0 bg-black bg-opacity-20"></div>
          
          {/* Avatar */}
          <div className="absolute -bottom-12 left-6">
            <div className="w-24 h-24 rounded-full border-4 border-white overflow-hidden bg-gray-200">
              {profile.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={`${profile.full_name || profile.username}'s avatar`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500 text-2xl font-bold">
                  {(profile.full_name || profile.username || 'U')[0].toUpperCase()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Profile Content */}
        <div className="pt-16 p-6">
          
          {/* Basic Info */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {profile.full_name || profile.username}
            </h1>
            
            {profile.username && profile.full_name && (
              <p className="text-gray-600 mb-2">@{profile.username}</p>
            )}

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
              
              {profile.location && (
                <div className="flex items-center space-x-1">
                  <MapPin className="h-4 w-4" />
                  <span>{profile.location}</span>
                </div>
              )}

              {profile.website && (
                <div className="flex items-center space-x-1">
                  <Globe className="h-4 w-4" />
                  <a 
                    href={profile.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-700 hover:underline"
                  >
                    Website
                  </a>
                </div>
              )}

              <div className="flex items-center space-x-1">
                <Calendar className="h-4 w-4" />
                <span>Joined {formatDate(profile.created_at)}</span>
              </div>
            </div>

            {profile.bio && (
              <p className="text-gray-700 leading-relaxed">{profile.bio}</p>
            )}
          </div>

          {/* Guide Profile Section */}
          {guideProfile && guideProfile.is_active && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="border-t pt-6"
            >
              <div className="bg-green-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-green-800 flex items-center space-x-2">
                    <Users className="h-6 w-6" />
                    <span>Tour Guide</span>
                  </h2>
                  
                  <div className="bg-green-100 px-3 py-1 rounded-full">
                    <span className="text-green-800 text-sm font-medium">Active Guide</span>
                  </div>
                </div>

                {guideProfile.description && (
                  <p className="text-gray-700 mb-4">{guideProfile.description}</p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  
                  {guideProfile.experience_years && (
                    <div className="text-center p-3 bg-white rounded-lg">
                      <Clock className="h-6 w-6 text-green-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-gray-900">
                        {guideProfile.experience_years}
                      </div>
                      <div className="text-sm text-gray-600">Years Experience</div>
                    </div>
                  )}

                  {guideProfile.rating && (
                    <div className="text-center p-3 bg-white rounded-lg">
                      <div className="flex justify-center mb-2">
                        {renderStars(guideProfile.rating)}
                      </div>
                      <div className="text-2xl font-bold text-gray-900">
                        {guideProfile.rating.toFixed(1)}
                      </div>
                      <div className="text-sm text-gray-600">Rating</div>
                    </div>
                  )}

                  {guideProfile.total_reviews > 0 && (
                    <div className="text-center p-3 bg-white rounded-lg">
                      <Users className="h-6 w-6 text-green-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-gray-900">
                        {guideProfile.total_reviews}
                      </div>
                      <div className="text-sm text-gray-600">Reviews</div>
                    </div>
                  )}

                  {guideProfile.hourly_rate && (
                    <div className="text-center p-3 bg-white rounded-lg">
                      <div className="text-2xl font-bold text-green-600 mb-2">
                        ${guideProfile.hourly_rate}
                      </div>
                      <div className="text-sm text-gray-600">Per Hour</div>
                    </div>
                  )}
                </div>

                {/* Specialties */}
                {guideProfile.specialties && guideProfile.specialties.length > 0 && (
                  <div className="mb-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {guideProfile.specialties.map((specialty, index) => (
                        <span
                          key={index}
                          className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Languages */}
                {guideProfile.languages && guideProfile.languages.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Languages</h3>
                    <div className="flex flex-wrap gap-2">
                      {guideProfile.languages.map((language, index) => (
                        <span
                          key={index}
                          className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                        >
                          {language}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default PublicProfileView